export default {
    intro: {
        headingFirst: "Modern",
        headingSecond: "Authenticity.",
        subheading: "Adobe talks to media and entertainment leaders about being real in a digital world.",
        paragraphText: "<p>Fakes, frauds, manipulators, shysters, marketers. For too long, the practice of marketing was synonymous with manipulation. That may have worked when a few networks controlled the news and wielded unquestioned authority, but today’s consumers are seeking authenticity and connection. Surrounded by information, they crave wisdom. Emboldened with choice and control, they reward brands who get real and truly know them.</p><p>\"Authenticity and credibility are things you can't buy,\" says Aubrey Flynn, VP of digital media at REVOLT Media & TV.</p><p>USA Today Network’s Kelly Andresen agrees. \"I know that's a big buzz word in the industry, but it actually does mean something to me.\"</p><p>Flynn and Andresen are just two of the many media and entertainment leaders who participated in <a href=\"http://foa.adobethinktank.com/?promoid=HM85XFF3&mv=other\" target=\"_blank\">Think Tank by Adobe</a> last fall in New York during Advertising Week, and their words captured the zeitgeist of the event.</p><p>\"I think that authenticity and an authentic voice are what people look for in any customer experience,\" says Maya Draisin, head of marketing at Wired magazine.</p><p>From the perspective of industry leaders, we examine authenticity — what it means, why it’s uniquely important today, and what role technology plays in revealing it. As you listen to these leaders, consider your own brand and voice, and what you can contribute to this ongoing conversation.</p>"
    },
    mobileTitles: [
        'Intro',
        'Voices',
        'Personalization',
        'Stories',
        'Tribes',
        'Technology',
        'Conclusion'
    ],
    articles: [
        {
            number: '1',
            color: '#F71C03',
            title: {
                highlightedText: 'The digital',
                normalText: ' revolution has changed us.'
            },
            paragraphText: '<p>Authenticity is more important today than ever. The digital revolution has changed us. Though younger generations never knew any different, it’s changed the way we interact with brands, changed what we expect. In today’s era of \"fake news,\" no one has more urgency to live and connect authentically than media and entertainment brands. So while today’s consumers have media distrust seared into their DNA, they’re still hoping, still looking for the voice that will speak to them, that will help them find their way.</p><p>And now, they’re looking for those voices to come from the brands they use.</p>',
            quotes: [
                {
                    heading: '\"They really want to find purpose.\"',
                    subheading: 'Jill Cress, CMO, National Geographic Partners',
                    stageQuote: {
                        text: '<p>What am I gonna do to <span>make&nbsp;this</span> world a better place?\”</p>',
                        author: 'Jill Cress',
                        accentColor: '#AFFDE7',
                        image: 'stage_quote_cress.jpg',
                        logo: 'nat-geo'
                    },
                    audioFile: '1_1_jill_cress.mp3',
                    color: '#F71602'
                },
                {
                    heading: 'Richard Edelman',
                    subheading: 'CEO, Edelman',
                    stageQuote: {
                        text: '<p>I <span>will&nbsp;not</span> buy a brand unless it speaks to issues that matter to me.\”</p>',
                        author: 'Richard Edelman',
                        accentColor: '#AFD5FD',
                        image: 'stage_quote_edelman.jpg',
                        logo: 'edelman'
                    },
                    audioFile: '1_2_richard_edelmen.mp3',
                    color: '#F24601'
                },
                {
                    heading: 'Maya Draisin',
                    subheading: 'head of marketing, Wired',
                    stageQuote: {
                        text: '<p>To also have <span>a&nbsp;voice</span> of their own.\”</p>',
                        author: 'Maya Draisin',
                        accentColor: '#CFD00A',
                        image: 'stage_quote_draisin.jpg',
                        logo: 'wired'
                    },
                    audioFile: '1_3_maya_draisin.mp3',
                    color: '#FE8C03'
                }
            ],
            conclusion: {
              image: 'diamond-purple.png',
              text: 'IT\'S ABOUT<br> PERSONAL<br> ENGAGEMENT,<br> NOT<br> BROADCASTING.'
            }
        },
        {
            number: '2',
            color: '#9010A3',
            title: {
                highlightedText: 'Talking',
                normalText: ' to a million individual human beings.'
            },
            paragraphText: '<p>\"Life is much more complicated and simpler than it seems,\" says Graceann Bennett, chief solutions officer of Dow Jones. \"I think the simple part is that we\'re talking to human beings.\" The complicated part is that most brands are trying to talk to millions of human beings at once. Technology has convinced us that brands can and should speak to us one-to-one because the technology — like machine learning and automation — exists to make such conversations possible.</p><p>But talking to a million customers and talking honestly and authentically with a million human beings aren’t the same thing.</p>',
            quotes: [
                {
                    heading: '\"Can you automate love?\"',
                    subheading: 'Sharmilan Rayer, VP of audience and programmatic, NBCUniversal',
                    stageQuote: {
                        text: '<p>Can you <span>automate</span> love?\”</p>',
                        author: 'Sharmilan Rayer',
                        accentColor: '#AFFDE7',
                        image: 'stage_quote_rayer.jpg',
                        logo: 'nbc-universal'
                    },
                    audioFile: '2_1_sharmilan_rayer.mp3',
                    color: '#9010A3',
                },
                {
                    heading: 'Graceann Bennett',
                    subheading: 'chief solutions officer, Dow Jones',
                    stageQuote: {
                        text: '<p>See <span>the&nbsp;human</span> behind the ones and zeroes.\”</p>',
                        author: 'Graceann Bennett',
                        accentColor: '#FA9087',
                        image: 'stage_quote_bennett.jpg',
                        logo: 'dow-jones'
                    },
                    audioFile: '2_2_graceann_bennett.mp3',
                    color: '#3200AA',
                },
                {
                    heading: 'Jill Cress',
                    subheading: 'CMO, National Geographic Partners',
                    stageQuote: {
                        text: '<p>We\'re going to have to <span>understand</span> those audiences in a much richer way.\”</p>',
                        author: 'Jill Cress',
                        accentColor: '#82F0E3',
                        image: 'stage_quote_cress.jpg',
                        logo: 'nat-geo'
                    },
                    audioFile: '2_3_jill_cress.mp3',
                    color: '#CC007D'
                },
                {
                    heading: 'Brian Benedik',
                    subheading: 'global head of sales, Spotify',
                    stageQuote: {
                        text: '<p>We\'re a <span>people-based</span> marketing platform.\”</p>',
                        author: 'Brian Benedik',
                        accentColor: '#AFD5FD',
                        image: 'stage_quote_benedik.jpg',
                        logo: 'spotify'
                    },
                    audioFile: '2_4_brian_benedik.mp3',
                    color: '#F71602',
                },
                {
                    heading: 'Maya Draisin',
                    subheading: 'head of marketing, Wired',
                    stageQuote: {
                        text: '<p>Tell a story that <span>works&nbsp;for&nbsp;them</span> at 7am and 7pm.\”</p>',
                        author: 'Maya Draisin',
                        accentColor: '#FFBE71',
                        image: 'stage_quote_draisin.jpg',
                        logo: 'wired'
                    },
                    audioFile: '2_5_maya_draisin.mp3',
                    color: '#9010A3',
                }
            ],
            conclusion: {
              image: 'diamond-green.png',
              text: 'IT\'S ABOUT<br> DISPLAYING THE<br> REAL YOU, NOT A<br> LIST OF PRETTY<br> VALUES.'
            }
        },
        {
            number: '3',
            color: '#5BC9BC',
            title: {
                highlightedText: 'The way',
                normalText: ' we tell stories.'
            },
            paragraphText: '<p>For today’s consumer, authenticity means being both personal and intimately tied to purpose. More and more, people want brands who stand for something, and the way they talk about what they stand for matters. If they don’t speak to our emotions as much as they speak to our logic, they sound fake, out of touch. Finding the balance between the intellectual power of technology and the passion of the human heart is the key. And it all comes down to storytelling.</p><p>When we engage with brands, the way they tell stories to us individually is just as important as the stories they choose to tell.</p>',
            quotes: [
                {
                    heading: '\"The key is to tell good stories.\"',
                    subheading: 'Maya Draisin, head of marketing, Wired',
                    stageQuote: {
                        text: '<p>The key is to tell <span>good&nbsp;stories</span>.\”</p>',
                        author: 'Maya Draisin',
                        accentColor: '#AFFDE7',
                        image: 'stage_quote_draisin.jpg',
                        logo: 'wired'
                    },
                    audioFile: '3_1_maya_draisin.mp3',
                    color: '#5BC9BC',
                },
                {
                    heading: 'Karthic Bala',
                    subheading: 'head of data strategy, Condé Nast',
                    stageQuote: {
                        text: '<p>The right <span>message</span> to the right person at the right time.\”</p>',
                        author: 'Karthic Bala',
                        accentColor: '#FFBE71',
                        image: 'stage_quote_bala.jpg',
                        logo: 'conde-nast'
                    },
                    audioFile: '3_2_karthic_bala.mp3',
                    color: '#0F60E7',
                },
                {
                    heading: 'Jill Cress',
                    subheading: 'CMO, National Geographic Partners',
                    stageQuote: {
                        text: '<p>Shift from storytelling to <span>story-doing</span>, so you\'re actually living your brand.\”</p>',
                        author: 'Jill Cress',
                        accentColor: '#fa8f87',
                        image: 'stage_quote_cress.jpg',
                        logo: 'nat-geo'
                    },
                    audioFile: '3_3_jill_cress.mp3',
                    color: '#3200AA'
                },
                {
                    heading: 'Graceann Bennett',
                    subheading: 'chief solutions officer, Dow Jones',
                    stageQuote: {
                        text: '<p><span>Deeds</span>, not words.\”</p>',
                        author: 'Graceann Bennett',
                        accentColor: '#82F0E3',
                        image: 'stage_quote_bennett.jpg',
                        logo: 'dow-jones'
                    },
                    audioFile: '3_4_graceann_bennett.mp3',
                    color: '#CC007D'
                },
                {
                    heading: 'Aubrey Flynn',
                    subheading: 'VP of digital media, REVOLT Media & TV',
                    stageQuote: {
                        text: '<p>How do we not just <span>tell</span> the story but be the story?\”</p>',
                        author: 'Aubrey Flynn',
                        accentColor: '#D9D435',
                        image: 'stage_quote_flynn.jpg',
                        logo: 'revolt'
                    },
                    audioFile: '3_5_aubrey_flynn.mp3',
                    color: '#FE8C03'
                }
            ],
            conclusion: {
              image: 'diamond-red.png',
              text: 'IT\'S ABOUT<br> LISTENING,<br> NOT<br> PROFESSING.'
            }
        },
        {
            number: '4',
            color: '#CC007D',
            title: {
                highlightedText: 'Finding',
                normalText: ' a tribe.'
            },
            paragraphText: '<p>In many ways, technology isolates us, it pulls us apart. And yet it can also bring us together. Through social media and other innovations, It makes it possible for us to find people who see the world like we do, people who may live on the other side of the world, who we would never have met otherwise. The stories we tell help us find community, a place where we find purpose, identity, and belonging. And we expect our favorite brands to do the same. We yearn to be treated as partners, not passive consumers. We want a seat at the table and the chance to take part in the story. But this goes far beyond social media. When companies live their brand and invite us to join them, they empower us to be a part of something bigger.</p><p>The authentic brand understands this and makes it possible.</p>',
            quotes: [
                {
                    heading: '\"You want an engaged consumer.\"',
                    subheading: 'Richard Edelman, CEO, Edelman',
                    stageQuote: {
                        text: '<p>You want an <span>engaged</span> consumer.\”</p>',
                        author: 'Richard Edelman',
                        accentColor: '#AFFDE7',
                        image: 'stage_quote_edelman.jpg',
                        logo: 'edelman'
                    },
                    audioFile: '4_1_richard_edelman.mp3',
                    color: '#CC007D',
                },
                {
                    heading: 'Maya Draisin',
                    subheading: 'head of marketing, Wired',
                    stageQuote: {
                        text: '<p>They allow people <span>a&nbsp;voice</span> all the time.\”</p>',
                        author: 'Maya Draisin',
                        accentColor: '#FFBE71',
                        image: 'stage_quote_draisin.jpg',
                        logo: 'wired'
                    },
                    audioFile: '4_2_maya_draisin.mp3',
                    color: '#9010A3',
                },
                {
                    heading: 'Aubrey Flynn',
                    subheading: 'VP of digital media, REVOLT Media & TV',
                    stageQuote: {
                        text: '<p>How does a brand effectively <span>help&nbsp;empower</span> that?\”</p>',
                        author: 'Aubrey Flynn',
                        accentColor: '#FFBE71',
                        image: 'stage_quote_flynn.jpg',
                        logo: 'revolt'
                    },
                    audioFile: '4_3_aubrey_flynn.mp3',
                    color: '#5BC9BC'
                }
            ],
            conclusion: {
                image: 'diamond-blue.png',
                text: 'IT\'S ABOUT<br> STORY-DOING,<br> NOT<br> STORY-TELLING'
            }
        },
        {
            number: '5',
            color: '#CC007D',
            title: {
                highlightedText: 'More than',
                normalText: ' technology.'
            },
            paragraphText: '<p>It\'s a new world. Technology has changed both our expectations as consumers and our ability as brands to deliver on those expectations. But change never stops and this new world is still evolving, still pushing the boundaries of what’s possible, of what we should and can expect. And as always, that change is driving — and being driven by — technological innovation, whether that’s the ability to handle massive amounts of data, machine learning, or virtual reality. But authenticity doesn’t come from technology. \"It\'s not about technology,\" Karthic Bala of Condé Nast says. \"It\'s not about changing landscapes. Ultimately, you have a customer. It\'s about customer focus.\"</p><p>So the technology that’s emerging now — and the technology that\'s still to come — must help brands find and focus on the customer.</p>',
            quotes: [
                {
                    heading: '\"The amount of data we capture is mind boggling.\"',
                    subheading: 'Karthic Bala, head of data strategy, Condé Nast',
                    stageQuote: {
                        text: '<p>The amount of data we capture is <span>mind&nbsp;boggling</span>.\”</p>',
                        author: 'Karthic Bala',
                        accentColor: '#AFFDE7',
                        image: 'stage_quote_bala.jpg',
                        logo: 'conde-nast'
                    },
                    audioFile: '5_1_karthic_bala.mp3',
                    color: '#3200AA',
                },
                {
                    heading: 'Brian Benedik',
                    subheading: 'global head of sales, Spotify',
                    stageQuote: {
                        text: '<p>We\'re using all of that to <span>enhance</span> the experience.\”</p>',
                        author: 'Brian Benedik',
                        accentColor: '#FFBE71',
                        image: 'stage_quote_benedik.jpg',
                        logo: 'spotify'
                    },
                    audioFile: '5_2_brian_benedik.mp3',
                    color: '#0F60E7',
                },
                {
                    heading: 'Graceann Bennett',
                    subheading: 'chief solutions officer, Dow Jones',
                    stageQuote: {
                        text: '<p>They want to be in <span>immersive</span>, rich environments.\”</p>',
                        author: 'Graceann Bennett',
                        accentColor: '#FFBE71',
                        image: 'stage_quote_bennett.jpg',
                        logo: 'dow-jones'
                    },
                    audioFile: '5_3_graceann_bennett.mp3',
                    color: '#9010A3'
                },
                {
                    heading: 'Kelly Andresen',
                    subheading: 'SVP, head of GET Creative, USA Today Network',
                    stageQuote: {
                        text: '<p>VR is the <span>future</span> of storytelling.\”</p>',
                        author: 'Kelly Andresen',
                        accentColor: '#FA9087',
                        image: 'stage_quote_andresen.jpg',
                        logo: 'usa-today'
                    },
                    audioFile: '5_4_kelly_andreson.mp3',
                    color: '#3200AA'
                }
            ],
            conclusion: {
                image: 'diamond-orange.png',
                text: 'IT\'S ABOUT<br> TRUST,<br> NOT<br> MANIPULATION'
            }
        }
    ]
}



// WEBPACK FOOTER //
// ./src/content/index.js